import styles from './search-filters.module.css'

import React, { useLayoutEffect, useRef } from 'react'
import cuid from 'cuid'

import type { FilterDefinition, FilterState } from 'types/filter-page'

import { Button } from 'components/ds'

import AddFilter from './add-filter'
import FilterBadge from './filter-badge'

type ClearAllFilterProps = {
  definitions: FilterDefinition[]
  onChange: (_value: FilterState[]) => void
  disabled?: boolean
  seedDefaultState?: boolean
}

type SearchFilterProps = {
  definitions: FilterDefinition[]
  value: FilterState[]
  onChange: (_value: FilterState[]) => void
}

type SelectedFilterBadgesProps = SearchFilterProps & {
  seedDefaultState?: boolean
}

export const FilterActionAddButton = ({
  definitions,
  value,
  onChange,
}: SearchFilterProps): React.ReactNode => {
  const handleAddFilter = (definition: FilterDefinition) =>
    onChange([
      ...value,
      {
        id: cuid(),
        definition,
        operator: undefined,
        value: undefined,
      },
    ])

  return (
    <AddFilter
      definitions={definitions}
      onAddFilter={handleAddFilter}
      currentFilterStates={value}
    />
  )
}

export const FilterActionClearAllButton = ({
  onChange,
  definitions,
  disabled,
  seedDefaultState = false,
}: ClearAllFilterProps): React.ReactNode => {
  // On clear all, remove all non-fixed filters and clear values of fixed filters
  // Applies to all filters values, including those that are hidden
  const handleClearAll = () => {
    if (!seedDefaultState) {
      onChange([])
      return
    }

    const fixedFilters = definitions.filter(filter => filter.fixed)
    const clearedFilters = fixedFilters.map(filter => ({
      id: cuid(),
      definition: filter,
      operator: undefined,
      value: undefined,
    }))
    onChange(clearedFilters)
  }

  return (
    <Button
      variant="ghost"
      size="2"
      className={styles.clearAllButton}
      onClick={handleClearAll}
      disabled={disabled}
    >
      Clear all
    </Button>
  )
}
export const SelectedFilterBadges = ({
  value,
  onChange,
  definitions,
  seedDefaultState = false,
}: SelectedFilterBadgesProps) => {
  const hasSeeded = useRef(false)

  useLayoutEffect(() => {
    if (!seedDefaultState) {
      return
    }
    if (hasSeeded.current || value.length) return
    hasSeeded.current = true

    const fixedFilters = definitions.filter(filter => filter.fixed)
    onChange(
      fixedFilters.map(filter => ({
        id: cuid(),
        definition: filter,
        operator: undefined,
        value: undefined,
      })),
    )
  }, [])

  const handleFilterChange = (index: number, newValue: FilterState): void => {
    const newFilters = [...value]
    const updatedValue = { ...newValue }
    const trimmedValue =
      typeof updatedValue.value === 'string'
        ? updatedValue.value.trim()
        : updatedValue.value
    updatedValue.value = trimmedValue

    const isValueInvalid =
      (typeof trimmedValue === 'string' && !trimmedValue.length) ||
      trimmedValue == null

    if (isValueInvalid) {
      updatedValue.operator = undefined
      updatedValue.value = undefined
    }

    newFilters[index] = updatedValue

    // if filter has no value and is not fixed by default, remove it
    if (isValueInvalid && !updatedValue.definition.fixed) {
      newFilters.splice(index, 1)
    }

    onChange(newFilters)
  }

  const handleFilterDelete = (index: number) => {
    if (!value.length) return null

    const newFilters = [...value]
    newFilters.splice(index, 1)
    onChange(newFilters)
  }
  // flatMap preserves original value[] indices for handleFilterChange/handleFilterDelete
  const filterBadges = value.flatMap((filterState, i) =>
    filterState.hidden
      ? []
      : [
          <FilterBadge
            key={
              filterState.id ??
              `${filterState.definition.field || filterState.definition.label}-${i}`
            }
            value={filterState}
            onChange={value => handleFilterChange(i, value)}
            onDelete={() => handleFilterDelete(i)}
          />,
        ],
  )

  return filterBadges
}
